import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCableMenu } from '@features/cable/hooks/useCableMenu';
import { devicesApi, useDeviceNavigation } from '@features/devices/api';
import type { DeviceNavigationItem } from '@features/devices/types';
import { getMenuItemsWithCounts, getDefaultDevicesMenuItems } from '@shared/config/menuConfigExtended';
import { useTasksCount } from '@shared/hooks/useTasksCount';
import { useMessagesCount } from '@shared/hooks/useMessagesCount';
import { useWarehouseMenu } from '@shared/hooks/useWarehouseMenu';
import { useTaskMenu } from '@shared/hooks/useTaskMenu';
import { useMapCities } from '@features/map/hooks/useMapCities';
import { MenuSearchForm } from '@shared/components/Menu/MenuSearchForm';
import { pruneDuplicateDeviceNavigationItems, transformNavigationItems } from '@shared/utils/deviceNavigationUtils';
import { deduplicateMenuItems } from '@shared/utils/menuNormalization';
import { filterMenuByPermissions } from '@shared/utils/menuPermissionFilter';
import { filterMenuByERPProfile } from '@shared/utils/menuERPProfileFilter';
import type { AntdMenuItem, ExtendedMenuItem } from '@shared/types/menu';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useActivitySources } from '@features/customers/hooks/useActivitySources';
import {
  erpProfileNavigationForMenu,
  useERPProfileNavigation,
} from '@shared/hooks/useERPProfileNavigation';

export function buildDeviceQuickSearchItems(
  settings: { quickSearchById?: boolean; quickSearchByInventoryNumber?: boolean } | undefined,
  translate: (key: string) => string,
  resolveByInventoryNumber: (value: string) => Promise<number>,
): AntdMenuItem[] {
  const items: AntdMenuItem[] = [];
  if (settings?.quickSearchById) {
    items.push({
      key: 'devices-search-id',
      label: React.createElement(MenuSearchForm, {
        type: 'device',
        placeholder: translate('menu:devices_search_by_id_placeholder'),
      }),
    });
  }
  if (settings?.quickSearchByInventoryNumber) {
    items.push({
      key: 'devices-search-inventory',
      label: React.createElement(MenuSearchForm, {
        type: 'device',
        fieldName: 'inventory_number',
        placeholder: translate('menu:devices_search_by_inventory_placeholder'),
        resolveDeviceByInventoryNumber: resolveByInventoryNumber,
      }),
    });
  }
  return items;
}

/**
 * Хук для подготовки данных меню сайдбара
 */
export function useSidebarMenu() {
  const { t } = useTranslation(['translation', 'devices', 'menu']);
  const { hasPermission, user } = useAuth();
  const erpProfileNavigationQuery = useERPProfileNavigation();
  const erpProfileNavigation = erpProfileNavigationForMenu(
    erpProfileNavigationQuery.data,
    erpProfileNavigationQuery.isError,
  );

  // Ролевой предикат для пунктов, чей роут защищён ролью (ProtectedRoute requiredRole).
  // Сравнение буквальное — как в ProtectedRoute и в backend RequireRole, чтобы гейт
  // меню и гейт роута расходиться не могли.
  const hasRole = React.useCallback(
    (role: string): boolean => Array.isArray(user?.roles) && user.roles.includes(role),
    [user],
  );

  // Загрузка данных для динамических меню
  const taskMenuQuery = useTaskMenu();
  const warehouseMenuQuery = useWarehouseMenu();
  const warehouseMenuData = warehouseMenuQuery.data;
  const cableMenuQuery = useCableMenu({
    enabled: hasPermission('cable_lines.view'),
  });
  const mapCitiesQuery = useMapCities();
  // Динамическое меню устройств тянется ТОЛЬКО при наличии права, которое требует
  // сама ручка: GET /api/v1/navigation/devices закрыт devices.view
  // (backend/internal/app/routeguard/dashboard_route_gate_test.go). Без права запрос
  // не должен уходить вовсе — глушить 403 после отправки нельзя (канон дашборда,
  // 57e7683b0). Поведение меню при этом не меняется: и раньше 403 приводил к
  // пустому deviceNavigationData и откату на статический defaultDeviceMenu.
  const { data: deviceNavigation } = useDeviceNavigation({
    enabled: hasPermission('devices.view'),
  });
  const deviceNavigationData = deviceNavigation?.data as DeviceNavigationItem[] | undefined;

  // Гейт пункта «Активность в интернете»: показываем, только если в инсталляции
  // есть источник этой метрики. Запрос уходит один раз на сессию и только при
  // праве экрана — ручка закрыта customers.view.
  const { data: activitySources } = useActivitySources({
    enabled: hasPermission('customers.view'),
  });
  const hasInternetActivitySource =
    activitySources?.settings?.hasInternetActivitySource ?? false;

  // Статусы загрузки
  const cableMenuStatus: 'loading' | 'error' | 'ready' = cableMenuQuery.isLoading
    ? 'loading'
    : cableMenuQuery.isError
      ? 'error'
      : 'ready';

  const warehouseMenuStatus: 'loading' | 'error' | 'ready' = warehouseMenuQuery.isLoading
    ? 'loading'
    : warehouseMenuQuery.isError || warehouseMenuData === null
      ? 'error'
      : 'ready';

  // Настройки навигации устройств
  const navigationSettings = deviceNavigation?.meta?.settings;
  const defaultDeviceMenu = useMemo(() => getDefaultDevicesMenuItems(t), [t]);

  // Фильтрация и дедупликация навигации устройств
  const filteredDeviceNavigation = useMemo(
    () => pruneDuplicateDeviceNavigationItems(deviceNavigationData),
    [deviceNavigationData],
  );

  // Трансформация навигации устройств в пункты меню
  const deviceNavigationItems = useMemo(
    () =>
      deduplicateMenuItems(
        transformNavigationItems(
          filteredDeviceNavigation,
          (key: string, defaultValue?: string) => t(key, defaultValue ?? key),
        ),
      ),
    [filteredDeviceNavigation, t],
  );

  // Подготовка дочерних элементов меню устройств
  const devicesMenuChildren = useMemo(() => {
    // V4: две формы идут непосредственно перед первым пунктом оборудования, без
    // выдуманной группы/заголовка «Быстрый поиск».
    const items = buildDeviceQuickSearchItems(
      navigationSettings,
      (key) => t(key),
      devicesApi.resolveByInventoryNumber,
    );

    if (deviceNavigationItems.length > 0) {
      items.push(...(deviceNavigationItems as AntdMenuItem[]));
    } else {
      items.push(...(defaultDeviceMenu.filter(Boolean) as AntdMenuItem[]));
    }

    // Всегда добавляем Каталог оборудования в конец списка, если он еще не добавлен
    const hasCatalog = items.some(item => item?.key === 'equipment-catalog-group');
    if (!hasCatalog) {
      const catalogItem = defaultDeviceMenu.find(item => item?.key === 'equipment-catalog-group');
      if (catalogItem) {
        items.push(catalogItem as AntdMenuItem);
      }
    }

    return items;
  }, [
    defaultDeviceMenu,
    deviceNavigationItems,
    navigationSettings?.quickSearchById,
    navigationSettings?.quickSearchByInventoryNumber,
    t,
  ]);

  // Счётчики
  const { data: tasksCount } = useTasksCount();
  const { data: messagesCount } = useMessagesCount();
  const taskMenu = taskMenuQuery.data ?? undefined;
  const warehouseMenu =
    warehouseMenuStatus === 'ready' && warehouseMenuData ? warehouseMenuData : undefined;

  // Статистика задач
  const tasksMenuStats = tasksCount
    ? { my: tasksCount.mine.total, all: tasksCount.all.total }
    : undefined;

  // Финальные пункты меню
  const menuItems = useMemo(
    () =>
      getMenuItemsWithCounts(
        t,
        {
          tasks: tasksMenuStats,
          messages: messagesCount,
        },
        {
          warehouseMenu,
          devicesChildren: devicesMenuChildren,
          warehouseStatus: warehouseMenuStatus,
          taskMenu,
          cableMenu: {
            status: cableMenuStatus,
            types: cableMenuQuery.data,
          },
          hasInternetActivitySource,
          mapCities: mapCitiesQuery.data && mapCitiesQuery.data.length > 0
            ? mapCitiesQuery.data
                .filter((city) => city.id != null && city.id !== undefined)
                .map((city) => ({
                  id: city.id,
                  name: city.name,
                  centerGeo: city.centerGeo,
                }))
            : undefined,
        },
      ),
    [
      t,
      tasksMenuStats,
      messagesCount,
      warehouseMenu,
      devicesMenuChildren,
      warehouseMenuStatus,
      taskMenu,
      cableMenuStatus,
      cableMenuQuery.data,
      mapCitiesQuery.data,
      hasInternetActivitySource,
    ],
  );

  // Нормализация пунктов меню
  const normalizedMenuItems = useMemo(
    () => {
      const deduped = deduplicateMenuItems(
        (menuItems ?? []).filter(Boolean) as AntdMenuItem[],
      ) as ExtendedMenuItem[];
      const rbacFiltered = filterMenuByPermissions(deduped, hasPermission, hasRole);
      return filterMenuByERPProfile(rbacFiltered, erpProfileNavigation) as AntdMenuItem[];
    },
    [menuItems, hasPermission, hasRole, erpProfileNavigation],
  );

  return {
    menuItems: normalizedMenuItems,
    // Статусы для обработки ошибок
    taskMenuQuery,
    warehouseMenuQuery,
    cableMenuQuery,
    warehouseMenuStatus,
    cableMenuStatus,
    erpProfileNavigationQuery,
    isWarehouseMenuEmpty:
      warehouseMenuStatus === 'ready' && (warehouseMenuData?.items?.length ?? 0) === 0,
  };
}
